import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  ExternalLink,
  FileText,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ErroApi } from '@/nucleo/auth/cliente-api';
import { formatarData, formatarDataHora } from '@/nucleo/formato/datas';
import { mascaraCpfCnpj, mascaraPlaca, mascaraTelefone } from '@/nucleo/formato/documentos';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { DetailWithTimeline } from '@/ui/DetailWithTimeline';
import { PageHeader } from '@/ui/PageHeader';
import { ProgressTimeline } from '@/ui/ProgressTimeline';
import { Badge, type BadgeProps } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { CardSkeleton } from '@/ui/base/table-skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import { GatingBanner } from '../comum/GatingBanner';
import { baixarComprovanteDeInscricao } from './certificado';
import { useMinhaInscricao } from './dados';
import { etapasDoCredenciamento } from './etapas';
import type {
  ComprovanteDeInscricao,
  Inscricao,
  MinhaInscricao,
  StatusDaInscricao,
} from './contrato';

/**
 * `/credenciado/inscricao` — acompanhamento da inscrição (ou renovação) de
 * credenciamento em nome do transportador. Mesma composição do protótipo
 * (`pages/portal/PortalInscricao.tsx`): cabeçalho, aviso de pendência
 * operacional, comprovantes por protocolo, atalho para a jornada completa e,
 * lado a lado, os dados da inscrição e a linha do tempo.
 *
 * Dado real, da API — ver `dados.ts`.
 */
/** Destino do atalho "Acompanhar inscrição" da faixa de pendência: aqui ele não aparece. */
const CAMINHO_DA_INSCRICAO = '/credenciado/inscricao';

export function MinhaInscricaoPage() {
  const { data, error, isLoading, isError, refetch } = useMinhaInscricao();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Minha Inscrição"
        description="Acompanhe o andamento do seu processo de credenciamento"
      />

      <GatingBanner telaAtual={CAMINHO_DA_INSCRICAO} />

      {error instanceof ErroApi && error.pedeOutroCaminho ? (
        // 403: perfil sem acesso ou cadastro sem vínculo. Tentar de novo não
        // resolve, então não há botão para isso; a mensagem vem da API.
        <ErrorState variant="forbidden" description={error.message} />
      ) : isError ? (
        <ErrorState
          variant="load-failed"
          description="Não foi possível carregar sua inscrição agora. Tente novamente em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      ) : isLoading ? (
        <div role="status" aria-live="polite" className="space-y-6">
          <CardSkeleton />
          <CardSkeleton />
          <p className="sr-only">Carregando sua inscrição.</p>
        </div>
      ) : !data ? (
        <EmptyState
          icon={FileText}
          title="Nenhuma inscrição encontrada"
          description="Não há inscrição de credenciamento vinculada ao seu cadastro."
        />
      ) : (
        <ConteudoDaInscricao dados={data} />
      )}
    </div>
  );
}

interface ConfiguracaoDoStatus {
  label: string;
  variant: NonNullable<BadgeProps['variant']>;
  icone: LucideIcon;
}

const STATUS: Record<StatusDaInscricao, ConfiguracaoDoStatus> = {
  RASCUNHO: { label: 'Rascunho', variant: 'secondary', icone: Clock },
  ENVIADO: { label: 'Enviada', variant: 'default', icone: Clock },
  PENDENTE_CONSENTIMENTO: {
    label: 'Pendente de consentimento',
    variant: 'outline',
    icone: AlertTriangle,
  },
  EM_ANALISE: { label: 'Em Análise', variant: 'default', icone: AlertTriangle },
  PRE_CREDENCIADO: { label: 'Aprovada', variant: 'default', icone: CheckCircle },
  HABILITADO: { label: 'Aprovada', variant: 'default', icone: CheckCircle },
  PENDENTE_DOCUMENTOS: {
    label: 'Pendente de documentos',
    variant: 'outline',
    icone: AlertTriangle,
  },
  AGUARDANDO_CORRECAO: { label: 'Aguardando correção', variant: 'outline', icone: AlertTriangle },
  REENVIADO: { label: 'Reenviada', variant: 'default', icone: Clock },
  EXPIRADO: { label: 'Expirada', variant: 'destructive', icone: XCircle },
  REPROVADO: { label: 'Reprovada', variant: 'destructive', icone: XCircle },
  DESISTENTE: { label: 'Desistente', variant: 'destructive', icone: XCircle },
};

function caminhoDaJornada(protocolo: string): string {
  return `/credenciados/acompanhamento/${encodeURIComponent(protocolo)}`;
}

function ConteudoDaInscricao({ dados }: { dados: MinhaInscricao }) {
  const { inscricao, comprovantes } = dados;
  const baixarComprovante = (comprovante: ComprovanteDeInscricao) => {
    try {
      baixarComprovanteDeInscricao(comprovante);
    } catch {
      toast.error('Não foi possível gerar o comprovante', {
        description: 'Tente novamente em instantes.',
      });
    }
  };

  return (
    <div className="space-y-6">
      {comprovantes.length > 0 && (
        <MeusComprovantes comprovantes={comprovantes} aoBaixar={baixarComprovante} />
      )}

      <Card className="rounded-2xl border-primary/30 bg-primary/5">
        <CardContent className="flex flex-col items-start justify-between gap-3 pt-6 sm:flex-row sm:items-center">
          <div className="space-y-1">
            <p className="text-sm font-semibold">Veja a jornada completa do seu credenciamento</p>
            <p className="text-xs text-muted-foreground">
              Documentos, pendências, histórico e próximas ações em uma única tela.
            </p>
          </div>
          <Button asChild size="sm">
            <Link
              to={caminhoDaJornada(inscricao.protocolo)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-4 w-4" aria-hidden="true" />
              Abrir jornada
            </Link>
          </Button>
        </CardContent>
      </Card>

      <DetailWithTimeline
        timeline={
          <Card className="rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Acompanhamento</CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressTimeline passos={etapasDoCredenciamento(inscricao)} variante="simples" />
            </CardContent>
          </Card>
        }
      >
        <DadosDaInscricao inscricao={inscricao} />
      </DetailWithTimeline>
    </div>
  );
}

function MeusComprovantes({
  comprovantes,
  aoBaixar,
}: {
  comprovantes: ComprovanteDeInscricao[];
  aoBaixar: (comprovante: ComprovanteDeInscricao) => void;
}) {
  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Meus comprovantes de inscrição</CardTitle>
        <p className="text-xs text-muted-foreground">
          {comprovantes.length === 1
            ? '1 inscrição enviada. Baixe o comprovante quando precisar.'
            : `${comprovantes.length} inscrições enviadas. Baixe o comprovante de cada veículo.`}
        </p>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {comprovantes.map((comprovante) => {
            const status = STATUS[comprovante.status_inscricao];
            const IconeDoStatus = status.icone;
            return (
              <li
                key={comprovante.id}
                className="flex flex-col gap-2 rounded-xl border border-border/60 bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{comprovante.protocolo}</span>
                    <Badge variant={status.variant} className="gap-1 rounded-md text-[10px]">
                      <IconeDoStatus className="h-3 w-3" aria-hidden="true" />
                      {status.label}
                    </Badge>
                    {comprovante.numero_placa && (
                      <Badge variant="outline" className="rounded-md text-[10px]">
                        {mascaraPlaca(comprovante.numero_placa)}
                      </Badge>
                    )}
                    {comprovante.modalidade && (
                      <Badge variant="secondary" className="rounded-md text-[10px]">
                        {comprovante.modalidade}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enviada em {formatarDataHora(comprovante.data_envio)}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="ghost" asChild>
                    <Link
                      to={caminhoDaJornada(comprovante.protocolo)}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Jornada do protocolo ${comprovante.protocolo}`}
                    >
                      <ExternalLink className="h-4 w-4 sm:mr-1.5" aria-hidden="true" />
                      <span className="hidden sm:inline">Jornada</span>
                    </Link>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => aoBaixar(comprovante)}
                    aria-label={`Comprovante do protocolo ${comprovante.protocolo}`}
                  >
                    <Download className="h-4 w-4 sm:mr-1.5" aria-hidden="true" />
                    <span className="hidden sm:inline">Comprovante</span>
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function DadosDaInscricao({ inscricao }: { inscricao: Inscricao }) {
  const status = STATUS[inscricao.status_inscricao];

  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-lg">Dados da Inscrição</CardTitle>
          <Badge variant={status.variant} className="rounded-lg">
            {status.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid gap-4 sm:grid-cols-2">
          <InfoItem label="Protocolo" value={inscricao.protocolo} />
          <InfoItem label="Nome / Razão Social" value={inscricao.nome_razao_social} />
          <InfoItem label="CPF/CNPJ" value={mascaraCpfCnpj(inscricao.cpf_cnpj)} />
          <InfoItem label="Tipo Pessoa" value={inscricao.tipo_pessoa} />
          <InfoItem label="Modalidade" value={inscricao.modalidade} />
          <InfoItem label="DRE" value={inscricao.dre_nome} />
          <InfoItem label="E-mail" value={inscricao.email} />
          <InfoItem label="Telefone" value={mascaraTelefone(inscricao.telefone)} />
          <InfoItem label="Data Envio" value={formatarDataHora(inscricao.data_envio)} />
          <InfoItem label="Data Análise" value={formatarDataHora(inscricao.data_analise)} />
        </dl>

        {inscricao.parecer && (
          <div className="mt-4 rounded-xl border border-border/50 bg-muted/50 p-4">
            <p className="mb-1 text-xs font-semibold text-muted-foreground">Parecer</p>
            <p className="text-sm">{inscricao.parecer}</p>
          </div>
        )}

        {inscricao.bloqueado_ate && (
          <div className="mt-2 rounded-xl border border-destructive/20 bg-destructive/5 p-3">
            <p className="text-sm text-destructive-text">
              Bloqueado até: {formatarData(inscricao.bloqueado_ate)}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function InfoItem({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium">{value || '—'}</dd>
    </div>
  );
}

export default MinhaInscricaoPage;
